import './styles/Loading.scss';

import { FC, HTMLAttributes, PropsWithChildren, ReactNode } from 'react';

const componentName = 'c-Loading';

interface LoadingProps extends HTMLAttributes<HTMLOrSVGElement> {
  size?: number;
  variant?: 'center' | 'inline';
  fill?: string;
}

interface OverlayProps extends Partial<PropsWithChildren> {
  zIndex?: number;
  visible?: boolean;
  children?: ReactNode;
  backgroundColor?: string;
  blur?: number;
}

export const Loading: FC<LoadingProps> & { Overlay: FC<OverlayProps> } = ({
  size = 32,
  variant = 'center',
  fill,
  ...rest
}) => {
  return (
    <div
      className={[`${componentName}`, `${componentName}--${variant}`]
        .join(' ')
        .trim()}
      style={{ height: size, width: size }}
    >
      <svg
        className={`${componentName}__grid`}
        width={size}
        height={size}
        viewBox="0 0 105 105"
        xmlns="http://www.w3.org/2000/svg"
        fill={fill}
        {...rest}
      >
        <circle cx="12.5" cy="12.5" r="12.5">
          <animate
            attributeName="fill-opacity"
            begin="0s"
            dur="1s"
            values="1;.2;1"
            calcMode="linear"
            repeatCount="indefinite"
          />
        </circle>
        <circle cx="12.5" cy="52.5" r="12.5" fillOpacity=".5">
          <animate
            attributeName="fill-opacity"
            begin="100ms"
            dur="1s"
            values="1;.2;1"
            calcMode="linear"
            repeatCount="indefinite"
          />
        </circle>
        <circle cx="52.5" cy="12.5" r="12.5">
          <animate
            attributeName="fill-opacity"
            begin="300ms"
            dur="1s"
            values="1;.2;1"
            calcMode="linear"
            repeatCount="indefinite"
          />
        </circle>
        <circle cx="52.5" cy="52.5" r="12.5">
          <animate
            attributeName="fill-opacity"
            begin="600ms"
            dur="1s"
            values="1;.2;1"
            calcMode="linear"
            repeatCount="indefinite"
          />
        </circle>
        <circle cx="92.5" cy="12.5" r="12.5">
          <animate
            attributeName="fill-opacity"
            begin="800ms"
            dur="1s"
            values="1;.2;1"
            calcMode="linear"
            repeatCount="indefinite"
          />
        </circle>
        <circle cx="92.5" cy="52.5" r="12.5">
          <animate
            attributeName="fill-opacity"
            begin="400ms"
            dur="1s"
            values="1;.2;1"
            calcMode="linear"
            repeatCount="indefinite"
          />
        </circle>
        <circle cx="12.5" cy="92.5" r="12.5">
          <animate
            attributeName="fill-opacity"
            begin="700ms"
            dur="1s"
            values="1;.2;1"
            calcMode="linear"
            repeatCount="indefinite"
          />
        </circle>
        <circle cx="52.5" cy="92.5" r="12.5">
          <animate
            attributeName="fill-opacity"
            begin="500ms"
            dur="1s"
            values="1;.2;1"
            calcMode="linear"
            repeatCount="indefinite"
          />
        </circle>
        <circle cx="92.5" cy="92.5" r="12.5">
          <animate
            attributeName="fill-opacity"
            begin="200ms"
            dur="1s"
            values="1;.2;1"
            calcMode="linear"
            repeatCount="indefinite"
          />
        </circle>
      </svg>
    </div>
  );
};

Loading.Overlay = (props: OverlayProps) => {
  const {
    zIndex,
    children,
    visible = true,
    backgroundColor,
    blur = 5,
    ...loadingProps
  } = props;

  if (!visible) {
    return null;
  }

  return (
    <div
      className="c-Loading__overlay"
      style={{
        zIndex: zIndex || 'inherit',
        ...(backgroundColor && { backgroundColor }),
        backdropFilter: `blur(${blur}px)`,
      }}
    >
      {children ? children : <Loading {...loadingProps} />}
    </div>
  );
};
